import { useState, type ReactNode } from 'react';

import { ACCENT_COLORS } from '../theme/palette.ts';
import { useTheme } from '../theme/themeContext.ts';

/**
 * The "Southwest EMR" mark, ported from `emr-po/branding/southwest-emr-logo-*.html` (the icon,
 * primary, and stacked lockups specifically — see that directory's own header for which of the six
 * files is canonical vs. a kept alternate). `BACKLOG.md` B4a already applied the palette; this adds
 * the mark itself.
 *
 * **Wing-line path updated 2026-08-24** to match `southwest-emr-logo-primary.html`'s revised mark
 * (a sharper zigzag replacing the earlier wave, and a single eye dot at the line's end rather than
 * one at each end) — pulled from that file specifically, since it's the only one of the six source
 * files updated as of this date; `-icon.html`/`-monochrome.html`/`-stacked.html` still carry the
 * prior design and should be re-checked once `emr-po` catches them up.
 *
 * **Fixed brand colours, not theme tokens.** The source palette's own `prefers-color-scheme` block
 * redefines `--bg`/`--ink`/`--badge-fill`/`--label-ink` for dark mode but deliberately leaves
 * `--line`, the three mesa tones, and `--accent` untouched in both modes — a brand mark keeps its
 * identity constant rather than drifting with a user's own accent pick the way `theme/palette.ts`'s
 * `ACCENT_COLORS` intentionally does for UI chrome. Only the ink (line-art fallback, unused here
 * directly) and the badge plate behind the icon switch with theme; everything else below is a
 * literal hex pulled from the source files, not a `--s-*`/`--p-*` reference.
 */

const MARK_LINE = '#2FA8A0';
const MARK_MESA_BACK = '#D9976B';
const MARK_MESA_MID = '#C1592B';
const MARK_MESA_FRONT = '#8F3D1D';
/** The wordmark's "EMR" segment. Unlike `MARK_LINE` above, this renders as real, readable DOM
 * text (it's the lockup's accessible name, so it can't be `aria-hidden` the way the icon is) —
 * the raw brand hex fails WCAG AA text contrast on the app's own light-mode surfaces (2.7:1 vs.
 * the 4.5:1 the pentest suite's axe check requires). Use the same already AA-verified teal
 * "accent text" swatch `tokens.css`/`palette.test.ts` maintain for teal accent text elsewhere,
 * rather than a third hand-picked hex. */
const WORDMARK_ACCENT: Record<'light' | 'dark', string> = {
  light: ACCENT_COLORS.teal.light.text,
  dark: ACCENT_COLORS.teal.dark.text,
};

const BADGE_FILL: Record<'light' | 'dark', string> = { light: '#EFE1CC', dark: '#2A1B12' };
const WORDMARK_INK: Record<'light' | 'dark', string> = { light: '#2A1B12', dark: '#F5EEE3' };

export interface LogoMarkProps {
  /** Rendered width and height in px. The icon is square. */
  readonly size?: number;
  /** Set to give the mark its own accessible name (e.g. a standalone favicon-style usage with no
   * adjacent visible wordmark). Omit when visible text beside it already names it — the default is
   * `aria-hidden`, matching how `ThemeMenu.tsx`'s own decorative swatch dot is marked. */
  readonly label?: string;
}

/** The badge icon alone — the bird-line and three mesa terraces, no wordmark. The square,
 * edge-to-edge "app-icon tile" variant from the same source lockup is what `scripts/generate-
 * favicons.mjs` rasterizes from `web/public/logo/tile.svg` instead — a static favicon has no theme
 * to react to, so it isn't this component. */
export function LogoMark({ size = 32, label }: LogoMarkProps) {
  const { resolvedMode } = useTheme();
  // The source ships two hand-tuned weights, not one shape linearly scaled: a heavier stroke and
  // larger dot at small sizes (the icon file's own "size ladder" section) so the wing line and the
  // single eye dot stay legible down to 16px, and a lighter, more refined weight once there is room
  // for it.
  const heavy = size <= 40;
  const strokeWidth = heavy ? 7 : 4.5;
  const dotRadius = heavy ? 4.5 : 3;
  const accessibility =
    label === undefined ? { 'aria-hidden': 'true' as const } : { role: 'img', 'aria-label': label };

  return (
    <svg width={size} height={size} viewBox="0 0 120 120" {...accessibility}>
      <rect x={4} y={4} width={112} height={112} rx={30} fill={BADGE_FILL[resolvedMode]} />
      <path
        d="M18,40 L34,40 L42,32 L50,54 L58,14 L66,40 L102,36"
        fill="none"
        stroke={MARK_LINE}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={102} cy={36} r={dotRadius} fill={MARK_LINE} />
      <polygon points="12,94 34,62 86,62 108,94" fill={MARK_MESA_BACK} />
      <polygon points="6,102 30,74 90,74 114,102" fill={MARK_MESA_MID} />
      <polygon points="4,112 24,86 96,86 116,112" fill={MARK_MESA_FRONT} />
    </svg>
  );
}

export interface LogoLockupProps {
  /** The icon's own size; the wordmark scales off it, matching the source lockup's ~0.58 ratio of
   * wordmark font-size to icon size (76px icon / 44px wordmark in the primary lockup). */
  readonly size?: number;
  readonly className?: string;
  /**
   * A practice's own logo (`BACKLOG.md` B4), already resolved by the caller to whether it should
   * render at all — `null`/omitted for the product's own mark. **Entitlement is the caller's job,
   * not this component's**: `AppShell.tsx` is the one place that checks `isEntitled(tier,
   * 'whiteLabel')` before ever passing a URL in here, matching `navigation.ts`'s own
   * "capability/entitlement decided once, at the call site" shape rather than this component
   * re-deriving it. Falls back to the default mark on a load error (a dead link, a since-removed
   * asset) via `onError`, rather than rendering a broken-image icon in the app's own chrome.
   */
  readonly orgLogoUrl?: string | null;
}

/** Icon plus "Southwest EMR" wordmark, horizontal — the primary lockup, for header bars and the
 * sign-in page. The visible text is the accessible name; the icon beside it is decorative. When
 * `orgLogoUrl` is set, an `<img>` of the practice's own logo replaces the whole lockup — the logo is
 * different in kind from the accent colour (`claude-kit/rules/invariants.md`: a user never overrides
 * it, only the practice does), so there is no "mark stays, colour changes" middle ground here the
 * way `ThemeMenu.tsx`'s accent override has. */
export function LogoLockup({
  size = 32,
  className,
  orgLogoUrl = null,
}: LogoLockupProps): ReactNode {
  const { resolvedMode } = useTheme();
  const fontSize = Math.round(size * 0.58);
  const gap = Math.max(6, Math.round(size * 0.2));
  // Tracks a load failure for the *specific* URL currently passed in, per `useState`'s lazy
  // initializer plus the render-time reset below — a stale "broken" flag left over from a previous,
  // different `orgLogoUrl` (a practice fixes their URL, or `OrgBrandingSync.tsx` refetches a new
  // one) must not keep this component stuck on the fallback mark forever.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  if (orgLogoUrl !== null && orgLogoUrl !== failedUrl) {
    return (
      <img
        src={orgLogoUrl}
        alt="" // Decorative in the same sense the default mark's icon half is — the practice's own
        // name is already visible as real page/app chrome text elsewhere, and an org-supplied logo
        // has no reliable machine-readable name of its own to assert as alt text.
        aria-hidden="true"
        className={className}
        style={{ height: size, maxWidth: size * 6, objectFit: 'contain' }}
        onError={() => {
          // A dead or removed logo URL falls back to the product's own mark on the very next render
          // rather than leaving a broken-image glyph in the app's own chrome.
          setFailedUrl(orgLogoUrl);
        }}
      />
    );
  }

  return (
    <div className={className} style={{ display: 'flex', alignItems: 'center', gap }}>
      <LogoMark size={size} />
      <span
        style={{
          fontSize,
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: WORDMARK_INK[resolvedMode],
          whiteSpace: 'nowrap',
        }}
      >
        Southwest<span style={{ color: WORDMARK_ACCENT[resolvedMode], fontWeight: 500 }}> EMR</span>
      </span>
    </div>
  );
}
